/**
 * Детальная страница смены швейного цеха.
 */

import { useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import styled, { useTheme } from 'styled-components';
import {
  ActionPanel,
  Button,
  ConfirmModal,
  DataTable,
  ExternalPageCanvas,
  PageIsland,
} from '../uikit';
import type { IDataTableColumn } from '../uikit';
import { IconEdit } from '../uikit/components/icons';
import type { ShiftDTO, ShiftItemDTO } from '../api/shifts';
import {
  findShiftsByIds,
  updateFactShift,
  closeShift,
  SHIFT_STATUS_LABELS,
} from '../api/shifts';
import { toUserMessage } from '../api/client';
import { findProductsByIDs } from '../api/products';
import type { Product } from '../api/products';
import { findOrdersByIDs } from '../api/orders';
import { findEmployeesByIDs } from '../api/employees';
import { useAuth } from '../auth';
import { formatDate, formatDateTime } from '../utils/formatDate';
import { formatFullName } from '../utils/formatFullName';
import { formatInteger, formatMoney } from '../utils/formatNumber';

const INTEGER_RE = /^\d*$/;

const ErrorMessage = styled.div`
  margin-top: ${({ theme }) => theme.space[0]}px;
  font-size: ${({ theme }) => theme.fontSizes[3]}px;
  color: ${({ theme }) => theme.colors.error.main};
`;

const InlineInput = styled.input<{ $error?: boolean }>`
  width: 80px;
  padding: ${({ theme }) => theme.space[0]}px ${({ theme }) => theme.space[1]}px;
  font-size: ${({ theme }) => theme.fontSizes[5]}px;
  font-family: inherit;
  border: 1px solid
    ${({ theme, $error }) => ($error ? theme.colors.error.main : theme.colors.divider)};
  border-radius: ${({ theme }) => theme.borderRadius}px;
  background: ${({ theme }) => theme.colors.white};
  color: ${({ theme }) => theme.colors.text.primary};

  &:focus {
    outline: none;
    border-color: ${({ theme, $error }) =>
      $error ? theme.colors.error.main : theme.colors.primary.main};
  }

  ${({ theme }) => theme.device.mobile} {
    width: 100%;
  }
`;

const ErrorActions = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.space[1]}px;
  flex-wrap: wrap;
`;

const MetaBlock = styled.div`
  margin: 0 0 ${({ theme }) => theme.space[2]}px;
  font-size: ${({ theme }) => theme.fontSizes[5]}px;
  color: ${({ theme }) => theme.colors.text.secondary};

  p {
    margin: ${({ theme }) => theme.space[0]}px 0 0;
  }

  p:first-child {
    margin: 0;
  }
`;

const PageTitle = styled.h2`
  margin: 0 0 ${({ theme }) => theme.space[2]}px;
  font-size: ${({ theme }) => theme.fontSizes[9]}px;
  line-height: 1.5;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text.primary};
`;

const LoadingText = styled.p`
  margin: ${({ theme }) => theme.space[4]}px 0 0;
  color: ${({ theme }) => theme.colors.text.secondary};
  font-size: ${({ theme }) => theme.fontSizes[5]}px;
`;

const ActionsStack = styled.div`
  margin-top: ${({ theme }) => theme.space[2]}px;
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.space[1]}px;
`;

const EditButtonContent = styled.span`
  display: flex;
  align-items: center;
  gap: 6px;
`;

interface ShiftItemRow extends ShiftItemDTO {
  productName?: string;
  factQtyInput: string;
  factQtyError?: string;
}

function hasUnsavedOrInvalidFact(rows: ShiftItemRow[]): boolean {
  return rows.some((r) => {
    const raw = r.factQtyInput.trim();
    if (raw === '') {
      return true;
    }
    const v = parseInt(raw, 10);
    return !Number.isInteger(v) || v < 0 || v !== r.factQty;
  });
}

function getItemColumns(
  canEditFact: boolean,
  onFactQtyChange: (id: string, value: string) => void,
): IDataTableColumn<ShiftItemRow>[] {
  return [
    {
      key: 'productName',
      header: 'Изделие',
      sortable: false,
      fixedWidth: 200,
      cellStyle: { px: 2 },
      render: (value) => (value ? String(value) : '—'),
    },
    {
      key: 'planQty',
      header: 'План, шт',
      sortable: false,
      fixedWidth: 96,
      cellStyle: { px: 2 },
      render: (value) => formatInteger(value as number | null),
    },
    {
      key: 'factQty',
      header: 'Факт, шт',
      sortable: false,
      fixedWidth: canEditFact ? 120 : 80,
      cellStyle: { px: 2 },
      truncate: false,
      render: (_, item) => {
        if (!canEditFact) {
          return formatInteger(item.factQty);
        }
        return (
          <div>
            <InlineInput
              type="text"
              inputMode="numeric"
              value={item.factQtyInput}
              onChange={(e) => {
                const v = e.target.value;
                if (v === '' || INTEGER_RE.test(v)) {
                  onFactQtyChange(item.id, v);
                }
              }}
              aria-label={`Факт для позиции ${item.productName ?? item.id}`}
              $error={Boolean(item.factQtyError)}
              aria-invalid={Boolean(item.factQtyError)}
            />
            {item.factQtyError && (
              <ErrorMessage role="alert">{item.factQtyError}</ErrorMessage>
            )}
          </div>
        );
      },
    },
    {
      key: 'payment',
      header: 'Оплата, руб',
      sortable: false,
      fixedWidth: 120,
      cellStyle: { px: 2 },
      render: (value) => formatMoney(value as string | null),
    },
  ];
}

interface ShiftLocationState {
  employeeShiftsList?: string;
}

export function ShiftPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const theme = useTheme();

  const isManagerOrAdmin =
    Boolean(user?.isCompanyAdmin) ||
    user?.miniappRole === 'manager' ||
    user?.miniappRole === 'administrator';
  const isEmployeeOnly = user?.miniappRole === 'employee' && !isManagerOrAdmin;

  const shiftsListPath =
    (location.state as ShiftLocationState | null)?.employeeShiftsList ??
    (isEmployeeOnly ? '/shifts/upcoming' : '/shifts');

  const [shift, setShift] = useState<ShiftDTO | null>(null);
  const [orderDisplay, setOrderDisplay] = useState<string>('');
  const [employeeDisplay, setEmployeeDisplay] = useState<string>('');
  const [itemRows, setItemRows] = useState<ShiftItemRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [saveLoading, setSaveLoading] = useState(false);
  const [closeLoading, setCloseLoading] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [closeError, setCloseError] = useState<string | null>(null);
  const [closeConfirmOpen, setCloseConfirmOpen] = useState(false);

  // Смена принадлежит текущему пользователю (employee может редактировать только свою смену).
  // userId может приходить как userId или id из /api/identity/get.
  const currentUserId = user?.userId ?? (user as { id?: string } | undefined)?.id;
  const isOwner = Boolean(currentUserId && shift?.employeeId === currentUserId);

  // Employee на своей смене редактирует factQty; manager/admin — только просмотр
  const canEditFact = isOwner && !isManagerOrAdmin;

  const loadShift = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    setLoadError(null);
    try {
      const res = await findShiftsByIds([id]);
      const found = res.items[id] ?? null;
      setShift(found);

      if (found?.orderId || found?.employeeId) {
        const [ordersRes, employeesRes] = await Promise.allSettled([
          found.orderId ? findOrdersByIDs([found.orderId]) : Promise.resolve({ items: {} as Record<string, { title?: string }> }),
          found.employeeId ? findEmployeesByIDs([found.employeeId]) : Promise.resolve({ items: {} as Record<string, { familyName?: string; givenName?: string; patronymic?: string }> }),
        ]);
        const order = ordersRes.status === 'fulfilled' && found.orderId ? ordersRes.value.items[found.orderId] : undefined;
        const emp = employeesRes.status === 'fulfilled' && found.employeeId ? employeesRes.value.items[found.employeeId] : undefined;
        setOrderDisplay(order?.title ?? found.orderId ?? '');
        setEmployeeDisplay(
          emp
            ? formatFullName(emp.familyName, emp.givenName, emp.patronymic) || found.employeeId || ''
            : found.employeeId ?? '',
        );
      } else {
        setOrderDisplay('');
        setEmployeeDisplay('');
      }

      if (found?.items?.length) {
        const productIds = [...new Set(found.items.map((it) => it.productId))];
        const productsRes = await findProductsByIDs(productIds);
        const names: Record<string, string> = {};
        for (const [pid, p] of Object.entries(productsRes.items)) {
          names[pid] = (p as Product).name;
        }
        setItemRows(
          found.items.map((it) => ({
            ...it,
            productName: names[it.productId] ?? it.productId,
            factQtyInput: String(it.factQty),
          })),
        );
      } else {
        setItemRows([]);
      }
    } catch (err) {
      setShift(null);
      setOrderDisplay('');
      setEmployeeDisplay('');
      setItemRows([]);
      setLoadError(toUserMessage(err));
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    void loadShift();
  }, [loadShift]);

  const handleFactQtyChange = (itemId: string, value: string) => {
    setItemRows((prev) =>
      prev.map((row) =>
        row.id === itemId ? { ...row, factQtyInput: value, factQtyError: undefined } : row,
      ),
    );
  };

  const handleSaveFact = async () => {
    if (!id) return;
    setSaveError(null);

    let valid = true;
    const updatedRows = itemRows.map((row) => {
      const val = parseInt(row.factQtyInput, 10);
      if (row.factQtyInput === '' || !Number.isInteger(val) || val < 0) {
        valid = false;
        return { ...row, factQtyError: 'Введите число >= 0' };
      }
      return { ...row, factQtyError: undefined };
    });
    setItemRows(updatedRows);

    if (!valid) return;

    setSaveLoading(true);
    try {
      const updated = await updateFactShift({
        id,
        items: updatedRows.map((row) => ({
          id: row.id,
          factQty: parseInt(row.factQtyInput, 10),
        })),
      });
      setShift(updated);
      if (updated.items?.length) {
        const productIds = [...new Set(updated.items.map((i) => i.productId))];
        const productsRes = await findProductsByIDs(productIds);
        const names: Record<string, string> = {};
        for (const [pid, p] of Object.entries(productsRes.items)) {
          names[pid] = (p as Product).name;
        }
        setItemRows(
          updated.items.map((it) => ({
            ...it,
            productName: names[it.productId] ?? it.productId,
            factQtyInput: String(it.factQty),
            factQtyError: undefined,
          })),
        );
      } else {
        setItemRows([]);
      }
    } catch (err) {
      setSaveError(toUserMessage(err));
    } finally {
      setSaveLoading(false);
    }
  };

  const performClose = async () => {
    if (!id) return;
    setCloseError(null);
    setCloseLoading(true);
    try {
      await closeShift(id);
      setCloseConfirmOpen(false);
      await loadShift();
    } catch (err) {
      setCloseError(toUserMessage(err));
    } finally {
      setCloseLoading(false);
    }
  };

  const handleCloseClick = () => {
    setCloseError(null);
    if (canEditFact && hasUnsavedOrInvalidFact(itemRows)) {
      setCloseConfirmOpen(true);
      return;
    }
    void performClose();
  };

  const handleCloseConfirm = () => {
    void performClose();
  };

  if (loading) {
    return (
      <ExternalPageCanvas>
        <LoadingText>Загрузка…</LoadingText>
      </ExternalPageCanvas>
    );
  }

  if (!shift) {
    return (
      <ExternalPageCanvas>
        <ActionPanel defaultPath={shiftsListPath} />
        <PageIsland>
          <p style={{ margin: 0, color: theme.colors.text.secondary }}>
            {loadError ? loadError : 'Смена не найдена'}
          </p>
          {loadError && (
            <ErrorActions style={{ marginTop: theme.space[2] }}>
              <Button variant="filled" color="primary" onClick={() => void loadShift()}>
                Повторить
              </Button>
            </ErrorActions>
          )}
        </PageIsland>
      </ExternalPageCanvas>
    );
  }

  const statusLabel =
    SHIFT_STATUS_LABELS[shift.status as keyof typeof SHIFT_STATUS_LABELS] ?? shift.status;

  const canClose = isOwner && shift.closedAt === null && !isManagerOrAdmin;

  return (
    <ExternalPageCanvas>
      <ActionPanel
        defaultPath={shiftsListPath}
        rightAddon={
          isManagerOrAdmin ? (
            <Button
              variant="text"
              color="secondary"
              size="s"
              onClick={() => navigate(`/shifts/${id}/edit`)}
            >
              <EditButtonContent>
                <IconEdit />
                Редактировать
              </EditButtonContent>
            </Button>
          ) : undefined
        }
      />
      <PageIsland>
        {loadError && (
          <p
            role="alert"
            style={{ margin: `0 0 ${theme.space[2]}px`, color: theme.colors.error.main }}
          >
            {loadError}
          </p>
        )}
        <PageTitle>Смена</PageTitle>
        <MetaBlock>
          <p>Заказ: {orderDisplay || shift.orderId || '—'}</p>
          <p>Сотрудник: {employeeDisplay || shift.employeeId || '—'}</p>
          <p>Дата смены: {formatDate(shift.shiftDate)}</p>
          <p>Статус: {statusLabel}</p>
          {shift.description && <p>Описание: {shift.description}</p>}
          <p>Закрыта: {shift.closedAt ? formatDateTime(shift.closedAt) : 'нет'}</p>
        </MetaBlock>
      </PageIsland>

      <PageIsland title="Позиции">
        <DataTable
          data={itemRows}
          columns={getItemColumns(canEditFact, handleFactQtyChange)}
          settings={{ horizontalLine: true }}
          minHeight={100}
          placeholder={{ text: 'Нет позиций' }}
        />

        {canEditFact && (
          <ActionsStack>
            {saveError && (
              <ErrorMessage role="alert" aria-live="polite">
                {saveError}
              </ErrorMessage>
            )}
            <Button
              variant="filled"
              color="primary"
              size="s"
              onClick={handleSaveFact}
              loading={saveLoading}
            >
              Сохранить факт
            </Button>
          </ActionsStack>
        )}

        {canClose && (
          <ActionsStack>
            {closeError && (
              <ErrorMessage role="alert" aria-live="polite">
                {closeError}
              </ErrorMessage>
            )}
            <Button
              variant="filled"
              color="primary"
              size="s"
              onClick={handleCloseClick}
              loading={closeLoading}
            >
              Закрыть смену
            </Button>
          </ActionsStack>
        )}
      </PageIsland>

      <ConfirmModal
        open={closeConfirmOpen}
        onClose={() => setCloseConfirmOpen(false)}
        onConfirm={handleCloseConfirm}
        title="Закрыть смену?"
        message="Есть несохранённые изменения факта. Закрыть смену без сохранения?"
        confirmText="Закрыть"
        cancelText="Отмена"
        confirmColor="primary"
        loading={closeLoading}
      />
    </ExternalPageCanvas>
  );
}
